import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { FileText, Plus, Trash2, Edit, Loader2, Briefcase, Code, Palette, BarChart3, FolderKanban, Crown, Upload, Sparkles, Search, Gift } from 'lucide-react';
import { ThemeToggle } from '@/components/ThemeToggle';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  Technology: Code,
  Marketing: BarChart3,
  Design: Palette,
  Management: FolderKanban,
};

function ResumeCard({ resume, onEdit, onDelete }) {
  return (
    <div data-testid={`resume-card-${resume.id}`} className="bg-white border border-slate-200 p-6 transition-all hover:shadow-[4px_4px_0px_#0F172A] hover:-translate-y-1">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{resume.title}</h3>
          <p className="text-sm text-slate-500">Template: {resume.template}</p>
        </div>
        <div className="px-3 py-1 bg-[#FFEDD5] border border-[#FB923C] text-slate-900 text-sm font-bold">
          {resume.completeness_score}%
        </div>
      </div>
      <div className="mb-4">
        {resume.data?.personal_info?.name && <p className="text-sm font-medium text-slate-700">{resume.data.personal_info.name}</p>}
        {resume.data?.work_experience?.length > 0 && (
          <p className="text-xs text-slate-500 mt-1">
            {resume.data.work_experience[0].title}{resume.data.work_experience[0].company ? ` at ${resume.data.work_experience[0].company}` : ''}
          </p>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-4">Last updated: {new Date(resume.updated_at).toLocaleDateString()}</p>
      <div className="flex gap-2">
        <Button data-testid={`edit-resume-btn-${resume.id}`} onClick={() => onEdit(resume.id)} className="flex-1 bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-4 py-2 text-sm transition-all">
          <Edit className="w-4 h-4 mr-1" /> Edit
        </Button>
        <Button data-testid={`delete-resume-btn-${resume.id}`} onClick={() => onDelete(resume.id)} className="bg-white border border-red-200 text-red-600 hover:bg-red-50 rounded-none px-4 py-2 text-sm">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function ExampleCard({ example, creatingId, onUse }) {
  const IconComponent = CATEGORY_ICONS[example.category] || Briefcase;
  return (
    <div
      data-testid={`example-card-${example.id}`}
      className="border border-slate-200 p-5 hover:shadow-[4px_4px_0px_#0F172A] hover:-translate-y-1 transition-all cursor-pointer"
      onClick={() => onUse(example.id)}
    >
      <div className="flex items-start gap-3">
        <div className="bg-[#FFEDD5] p-2.5 border border-[#FB923C]">
          <IconComponent className="w-5 h-5 text-slate-900" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-slate-900 text-base" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{example.title}</h4>
          <p className="text-xs text-slate-500 mt-0.5">{example.category} &middot; {example.template} template</p>
          <p className="text-xs text-slate-600 mt-2 line-clamp-2">{example.data.professional_summary}</p>
        </div>
      </div>
      {creatingId === example.id && (
        <div className="mt-3 flex items-center gap-2 text-xs text-[#FB923C]">
          <Loader2 className="w-3 h-3 animate-spin" /> Creating...
        </div>
      )}
    </div>
  );
}

function EmptyState({ onShowExamples, onCreateNew }) {
  return (
    <div data-testid="empty-state" className="bg-white border border-slate-200 p-12 text-center">
      <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
      <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>No resumes yet</h3>
      <p className="text-base text-slate-600 mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Create a new resume or start from a professionally crafted example</p>
      <div className="flex justify-center gap-3">
        <Button data-testid="empty-example-btn" onClick={onShowExamples} className="bg-white border-2 border-slate-900 text-slate-900 font-bold rounded-none px-6 py-3 transition-all hover:bg-slate-50 shadow-[3px_3px_0px_#0F172A]">
          <Briefcase className="w-5 h-5 mr-2" /> Browse Examples
        </Button>
        <Button data-testid="empty-create-btn" onClick={onCreateNew} className="bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-6 py-3 transition-all hover:-translate-y-1 shadow-[4px_4px_0px_#0F172A] hover:shadow-[6px_6px_0px_#0F172A]">
          <Plus className="w-5 h-5 mr-2" /> Create Blank
        </Button>
      </div>
    </div>
  );
}

function ResumeList({ resumes, onEdit, onDelete }) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {resumes.map((resume) => (
        <ResumeCard key={resume.id} resume={resume} onEdit={onEdit} onDelete={onDelete} />
      ))}
    </div>
  );
}

function DashboardContent({ loading, resumes, onEdit, onDelete, onShowExamples, onCreateNew }) {
  if (loading) {
    return <div className="flex justify-center items-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#FB923C]" /></div>;
  }
  if (resumes.length === 0) {
    return <EmptyState onShowExamples={onShowExamples} onCreateNew={onCreateNew} />;
  }
  return <ResumeList resumes={resumes} onEdit={onEdit} onDelete={onDelete} />;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [examples, setExamples] = useState([]);
  const [showExamples, setShowExamples] = useState(false);
  const [creatingFromExample, setCreatingFromExample] = useState(null);

  const fetchResumes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/resumes`, { withCredentials: true });
      setResumes(data);
    } catch (error) {
      toast.error('Failed to load resumes. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchExamples = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/resume-examples`, { withCredentials: true });
      setExamples(data);
    } catch (error) {
      toast.error('Could not load resume examples.');
    }
  }, []);

  useEffect(() => {
    fetchResumes();
    fetchExamples();
  }, [fetchResumes, fetchExamples]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Are you sure you want to delete this resume?')) return;
    try {
      await axios.delete(`${API_URL}/api/resumes/${id}`, { withCredentials: true });
      setResumes((prev) => prev.filter(r => r.id !== id));
    } catch (error) {
      toast.error('Failed to delete resume.');
    }
  }, []);

  const handleEdit = useCallback((id) => {
    navigate(`/builder/${id}`);
  }, [navigate]);

  const handleUseExample = useCallback(async (exampleId) => {
    setCreatingFromExample(exampleId);
    try {
      const { data } = await axios.post(`${API_URL}/api/resumes/from-example`, { example_id: exampleId }, { withCredentials: true });
      setShowExamples(false);
      navigate(`/builder/${data.id}`);
    } catch (error) {
      toast.error('Failed to create resume from example.');
    } finally {
      setCreatingFromExample(null);
    }
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/');
  }, [logout, navigate]);

  const openExamples = useCallback(() => setShowExamples(true), []);
  const goToNewResume = useCallback(() => navigate('/builder/new'), [navigate]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <FileText className="w-8 h-8 text-[#FB923C]" strokeWidth={2} />
            <span className="text-2xl font-black text-slate-900 dark:text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Aarova AI</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 dark:text-slate-400 hidden md:block" data-testid="user-email">{user?.email}</span>
            <ThemeToggle />
            <Button data-testid="go-pro-btn" onClick={() => navigate('/subscription')} className="bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-4 py-2 text-sm transition-all shadow-[3px_3px_0px_#0F172A]">
              <Crown className="w-4 h-4 mr-1" /> Go Pro
            </Button>
            <Button data-testid="logout-btn" onClick={handleLogout} className="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 rounded-none px-4 py-2 text-sm">Log Out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-black text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} data-testid="dashboard-heading">My Resumes</h1>
            <p className="text-base text-slate-600 dark:text-slate-400" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Create and manage your professional resumes</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button data-testid="one-page-builder-btn" onClick={() => navigate('/one-page-builder')} className="bg-white dark:bg-slate-800 border-2 border-[#FB923C] text-slate-900 dark:text-slate-100 font-bold rounded-none px-5 py-3 transition-all hover:bg-[#FFEDD5] dark:hover:bg-slate-700 shadow-[3px_3px_0px_#FB923C]">
              <Sparkles className="w-5 h-5 mr-2" /> Fresher Resume
            </Button>
            <Button data-testid="job-search-btn" onClick={() => navigate('/job-search')} className="bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-5 py-3 transition-all shadow-[3px_3px_0px_#0F172A] hover:-translate-y-0.5">
              <Search className="w-5 h-5 mr-2" /> Find Jobs
            </Button>
            <Button data-testid="refer-earn-btn" onClick={() => navigate('/refer-and-earn')} className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-none px-5 py-3 transition-all shadow-[3px_3px_0px_#0F172A] hover:-translate-y-0.5">
              <Gift className="w-5 h-5 mr-2" /> Refer &amp; Earn 10%
            </Button>
            <Button data-testid="upload-resume-btn" onClick={() => navigate('/upload')} className="bg-white dark:bg-slate-800 border-2 border-slate-900 dark:border-slate-500 text-slate-900 dark:text-slate-100 font-bold rounded-none px-5 py-3 transition-all hover:bg-slate-50 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#38bdf8]">
              <Upload className="w-5 h-5 mr-2" /> Upload Resume
            </Button>
            <Button data-testid="use-example-btn" onClick={openExamples} className="bg-white dark:bg-slate-800 border-2 border-slate-900 dark:border-slate-500 text-slate-900 dark:text-slate-100 font-bold rounded-none px-5 py-3 transition-all hover:bg-slate-50 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#38bdf8]">
              <Briefcase className="w-5 h-5 mr-2" /> Use Example
            </Button>
            <Button data-testid="create-resume-btn" onClick={goToNewResume} className="bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-5 py-3 transition-all hover:-translate-y-1 shadow-[4px_4px_0px_#0F172A]">
              <Plus className="w-5 h-5 mr-2" /> New Resume
            </Button>
          </div>
        </div>

        <DashboardContent loading={loading} resumes={resumes} onEdit={handleEdit} onDelete={handleDelete} onShowExamples={openExamples} onCreateNew={goToNewResume} />
      </main>

      <Dialog open={showExamples} onOpenChange={setShowExamples}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Resume Examples</DialogTitle>
            <DialogDescription>Choose a professionally crafted example to start with. You can customize everything after.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            {examples.map((example) => (
              <ExampleCard key={example.id} example={example} creatingId={creatingFromExample} onUse={handleUseExample} />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
